import { Defaults } from "@/models/Defaults";
import { Partnership } from "@/models/Partnership";
import { Quote } from "@/models/Quote";
import axios from "axios";
import { DateTimeFormatter, LocalDate } from "@js-joda/core";
import { formatAmount, calculateQuoteTotal } from "@/utils/Utils";
import { PaymentOptions } from "@/models/QuoteInsurance";
import { User } from "@/models/User";
import { NssiData } from "./NssiData";
import { UserData } from "./UserData";
import instanceAxios from "./axiosInst";
export class EmailService {
  /**
   * Sends College/University partnership email.
   * @param partnership
   * @returns
   */
  static async sendCollegePartnershipInfo(
    partnership: Partnership
  ): Promise<{ error: boolean; message: string }> {
    const result = {
      error: false,
      message: "",
    };

    const query = {
      //method: "partnership",
      fullName: partnership.fullName,
      title: partnership.title,
      email: partnership.email,
      phone: partnership.phone,
      university: partnership.college,
      city: partnership.city,
      state: partnership.state,
      nrOfStudents: partnership.nrOfStudents,
      typeOfInfo: this.typeOfInfo(partnership),
      comments: partnership.comments,
      //recipients: process.env.VUE_APP_EMAILS_PARTNERSHIP_TO.split(",")
      recipients: process.env.VUE_APP_EMAILS_PARTNERSHIP_TO,
    };

    let response;
    try {
      response = await axios.post(
        process.env.VUE_APP_MAGENTO_URL +
          "/rest/portal/V1/emailService/partnership",
        query
      );
    } catch (error) {
      result.error = true;
      result.message = "Unknown error";
    }
    return new Promise<{ error: boolean; message: string }>((resolve) => {
      resolve(result);
    });
  }

  static typeOfInfo(partnership: Partnership): string {
    let info = "";
    if (partnership.infoGeneral) {
      info += "General Information";
    }
    if (partnership.infoPrint) {
      if (info) {
        info += ", ";
      }
      info += "Print Materials";
    }
    if (partnership.infoDigital) {
      if (info) {
        info += ", ";
      }
      info += "Digital Materials";
    }
    return info;
  }

  /**
   * Sends Order Receipt email to the user.
   * @param quote
   * @param user
   * @returns
   */
  static async sendOrderReceipt(
    quote: Quote,
    user: User,
    applicationLink: string,
    orderLink: string
  ): Promise<{ error: boolean; message: string }> {
    const result = {
      error: false,
      message: "",
    };

    const query: any = {
      method: "receipt",
      orderDate: new Date().toLocaleDateString(),
      insuredFullName:
        quote.checkout.info.firstName + " " + quote.checkout.info.lastName,
      policyTerm: Defaults.getInstance().policyLength + " Year",
      assurantOrderId: quote.personalInsurance.transactionId,
      assurantCoverage:
        formatAmount(
          quote.rentersInsurance.quoteCoverageDetails.liabilityAmount
        ) +
        "/" +
        formatAmount(
          quote.rentersInsurance.quoteCoverageDetails.personalPropertyAmount
        ) +
        "/" +
        formatAmount(quote.rentersInsurance.dueToday) +
        " " +
        this.formatPaymentOption(quote.rentersInsurance.paymentOption),
      assurantStartDate: LocalDate.parse(quote.info.coverageStartDate).format(
        DateTimeFormatter.ofPattern("M/d/yyyy")
      ),
      //assurantPolicyNumber: quote.rentersInsurance.policyNumber,
      nssiOrderId: quote.hasPersonalInsurance
        ? quote.personalInsurance.transactionId
          ? quote.personalInsurance.transactionId
          : "N/A"
        : "N/A",
      nssiCoverage: quote.hasPersonalInsurance
        ? formatAmount(quote.personalInsurance.coverageAmount) +
          "/" +
          formatAmount(quote.personalInsurance.dueToday) +
          " " +
          this.formatPaymentOption(quote.personalInsurance.paymentOption)
        : "N/A",
      nssiStartDate: quote.hasPersonalInsurance
        ? LocalDate.now().format(DateTimeFormatter.ofPattern("M/d/yyyy"))
        : "N/A",
      //nssiPolicyNumber: quote.hasPersonalInsurance ? (quote.personalInsurance.policyNumber ? quote.personalInsurance.policyNumber : "N/A") : "N/A",
      billedTo: this.getBilledToInfo(quote),
      email: quote.checkout.info.email,
      // TODO: should this be this email or logged in user email?
      recipients: new Array(quote.checkout.info.email),
      applicationLink: applicationLink !== "" ? applicationLink : false,
      orderLink: orderLink !== "" ? orderLink : false,
    };

    if (!quote.nssiPassed) {
      query.nssiFailed = true;
    }

    let response;
    try {
      response = await instanceAxios.post(
        process.env.VUE_APP_MAGENTO_URL +
          "/rest/portal/V1/emailService/success/" +
          quote.rentersInsurance.policyNumber,
        query
      );
    } catch (error) {
      result.error = true;
      result.message = "Unknown error";
    }

    return new Promise<{ error: boolean; message: string }>((resolve) => {
      resolve(result);
    });
  }

  static formatPaymentOption(value: any) {
    const num = Number(value) as PaymentOptions;
    switch (num) {
      case PaymentOptions.Monthly:
        return "Monthly";
      case PaymentOptions.Annual:
        return "Annual";
      case PaymentOptions.FourPay:
        return "4 Pay";
      default:
        return "Unknown";
    }
  }

  static getBilledToInfo(quote: Quote): string {
    let result = "";
    const address = NssiData.getBillingAddress(quote);
    result += address.firstName + " " + address.lastName + "<br>";
    result +=
      address.address +
      ", " +
      address.city +
      ", " +
      address.state.title +
      ", " +
      address.zip +
      "<br>";
    result +=
      "Credit / Debit Card " +
      "**** " +
      quote.checkout.paymentMethod.cardNumber.substr(
        quote.checkout.paymentMethod.cardNumber.length - 4
      ) +
      "<br>";
    result += "Due Today: " + formatAmount(calculateQuoteTotal(quote));

    return result;
  }
}
